'use client';

import { useEffect, useRef, useState } from 'react';
import { loadDwtSdk, type DynamsoftStatic } from '@/lib/scanner/load-dwt';
import { randomId } from '@/lib/random';
import type { DiscoveredDevice } from '../types';
import { dedupeScannerDevices, normalizeScannerName } from './dwt-utils';

type DwtDevice = {
  name: string;
  displayName: string;
  deviceType: number;
};

type DwtInstance = {
  GetDevicesAsync(deviceType?: number, refresh?: boolean): Promise<DwtDevice[]>;
};

const INSTANCE_PREFIX = 'safha-device-discovery';

function makeInstanceId(): string {
  return `${INSTANCE_PREFIX}-${randomId()}`;
}

function formatError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object') {
    const obj = err as { code?: number; message?: string };
    if (typeof obj.message === 'string') {
      return obj.code != null ? `${obj.code}: ${obj.message}` : obj.message;
    }
  }
  return String(err);
}

export type ScannerDiscoveryStatus =
  'loading' | 'ready' | 'service-missing' | 'error';

export interface UseScannerDiscoveryResult {
  status: ScannerDiscoveryStatus;
  devices: DiscoveredDevice[];
  error: string | null;
  reload: () => void;
}

export function useScannerDiscovery(
  enabled: boolean
): UseScannerDiscoveryResult {
  const [status, setStatus] = useState<ScannerDiscoveryStatus>('loading');
  const [devices, setDevices] = useState<DiscoveredDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;
    const instanceId = makeInstanceId();
    let dynamsoftRef: DynamsoftStatic | null = null;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset when enabled/reloadKey changes
    setStatus('loading');

    setError(null);

    (async () => {
      try {
        const Dynamsoft = await loadDwtSdk();
        if (cancelledRef.current) return;
        dynamsoftRef = Dynamsoft;

        const dwt = await new Promise<DwtInstance>((resolve, reject) => {
          Dynamsoft.DWT.CreateDWTObjectEx(
            { WebTwainId: instanceId },
            resolve,
            (e) => reject(new Error(`${e.code}: ${e.message}`))
          );
        });
        if (cancelledRef.current) {
          try {
            Dynamsoft.DWT.DeleteDWTObject(instanceId);
          } catch {
            // ignore
          }
          return;
        }

        const rawList = await dwt.GetDevicesAsync(undefined, true);
        if (cancelledRef.current) return;

        // Same physical scanner can show up under multiple TWAIN/WIA bridges;
        // collapse those duplicates and use the canonical name.
        const list = dedupeScannerDevices(rawList);

        setDevices(
          list.map((d) => {
            const canonical = normalizeScannerName(d.displayName);
            return {
              device_type: 'scanner' as const,
              name: canonical,
              // Stable, immutable handle the backend stores so we can still
              // recognise this physical scanner after the operator renames
              // the human-readable Device.name.
              scanner_identifier: canonical,
              serial_number: null,
              ip_address: null,
              software_version: null
            };
          })
        );
        setStatus('ready');
      } catch (err) {
        if (cancelledRef.current) return;
        const message = formatError(err);
        // Dynamsoft surfaces "service not installed" via code -2800 and similar
        // bootstrap errors. Map any startup failure to the install-prompt UX.
        if (/-?28\d\d|service|installer/i.test(message)) {
          setStatus('service-missing');
        } else {
          setStatus('error');
        }
        setError(message);
      }
    })();

    return () => {
      cancelledRef.current = true;
      if (dynamsoftRef) {
        try {
          dynamsoftRef.DWT.DeleteDWTObject(instanceId);
        } catch {
          // ignore
        }
      }
    };
  }, [enabled, reloadKey]);

  return {
    status,
    devices,
    error,
    reload: () => setReloadKey((k) => k + 1)
  };
}
